import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import ProfileSection from "../settings/ProfileSection";
import SecuritySection from "../settings/SecuritySection";
import BrandSection from "../settings/BrandSection";
import NotificationsSection from "../settings/NotificationsSection";
import AppearanceSection from "../settings/AppearanceSection";
import OtherSection from "./settings/OtherSection";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/authSlice";
import { authStorage } from "../../utils/authStorage";



const SECTION_LIST = [
    { key: 'profile', label: 'Profile' },
    { key: 'security', label: 'Account Security' },
    // { key: 'brand', label: 'Business/Brand' },
    // { key: 'notifications', label: 'Notifications' },
    { key: 'appearance', label: 'Theme/Appearance' },
    // { key: 'other', label: 'Other' },
];

export default function SettingsModal({ isOpen, onClose }) {
    const dispatch = useDispatch();
    // Section navigation state
    const [section, setSection] = useState(null);

    // Removed profile reset logic to preserve fetched profile data

    // Reset section when modal closes
    React.useEffect(() => {
        if (!isOpen) setSection(null);
    }, [isOpen]);
    // ...existing state for each section...
    const [profile, setProfile] = useState({ firstName: '', lastName: '', email: '', phone: '', picture: null });
    const [profilePreview, setProfilePreview] = useState(null);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [twoFactor, setTwoFactor] = useState(false);
    const [business, setBusiness] = useState({ name: '', logo: null });
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoError, setLogoError] = useState("");
    const [emailNotif, setEmailNotif] = useState(true);
    const [smsNotif, setSmsNotif] = useState(false);
    const [systemAlerts, setSystemAlerts] = useState(true);
    const [language, setLanguage] = useState('en');
    // Safeguard: track logout state
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    if (!isOpen) return null;

    // ...handlers unchanged...
    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        // Map snake_case to camelCase for input fields
        let key = name;
        if (key === 'first_name') key = 'firstName';
        if (key === 'last_name') key = 'lastName';
        setProfile(prev => ({ ...prev, [key]: value }));
    };
    const handleProfilePicChange = (fileOrEvent) => {
        let file = null;

        // FileUploadField passes File directly; keep event fallback for compatibility.
        if (fileOrEvent instanceof File) {
            file = fileOrEvent;
        } else if (fileOrEvent && fileOrEvent.target && fileOrEvent.target.files) {
            file = fileOrEvent.target.files[0] || null;
        }

        setProfile((prev) => ({ ...prev, picture: file }));
        setProfilePreview(file ? URL.createObjectURL(file) : null);
    };

    // Update: Use toast for profile save success/error
    const handleSaveProfile = async () => {
        if (isSavingProfile) return;
        setIsSavingProfile(true);
        try {
            const { default: axiosInstance } = await import("../../services/axiosInstance");
            const { getCurrentUser } = await import("../../utils/authStorage");
            const user = getCurrentUser();
            if (user && user.id) {
                const formData = new FormData();
                formData.append("first_name", profile.firstName || "");
                formData.append("last_name", profile.lastName || "");
                formData.append("email", profile.email || "");
                formData.append("phone", profile.phone || "");

                if (profile.picture instanceof File) {
                    formData.append("picture", profile.picture);
                }
                if (profile.picture === null) {
                    formData.append("remove_picture", "1");
                }

                const res = await axiosInstance.put(`/admin/users/${user.id}/profile`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                if (res.status === 200) {
                    toast.success("Profile updated successfully!");
                    const updated = res.data?.data;
                    if (updated) {
                        const nextUser = {
                            ...user,
                            first_name: updated.first_name ?? user.first_name ?? null,
                            last_name: updated.last_name ?? user.last_name ?? null,
                            email: updated.email ?? user.email ?? null,
                            phone: updated.phone ?? user.phone ?? null,
                            image: updated.image ?? updated.picture ?? null,
                            picture: updated.picture ?? updated.image ?? null,
                        };
                        const token = authStorage.getToken();
                        if (token) {
                            dispatch(loginSuccess({ token, user: nextUser }));
                        } else {
                            authStorage.setUser(nextUser);
                        }

                        setProfile((prev) => ({
                            ...prev,
                            firstName: updated.first_name || prev.firstName,
                            lastName: updated.last_name || prev.lastName,
                            email: updated.email || prev.email,
                            phone: updated.phone || prev.phone,
                            picture: updated.picture ?? updated.image ?? null,
                        }));
                        setProfilePreview(null);
                    }
                    if (onClose) onClose();
                } else {
                    toast.error("Failed to update profile.");
                }
            }
        } catch (e) {
            toast.error("Error updating profile.");
        } finally {
            setIsSavingProfile(false);
        }
    };
    const handlePasswordChange = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setPasswordError("");
        // Validation checks
        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError("All fields are required.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("New passwords do not match.");
            return;
        }
        try {
            // Verify current password
            const { verifyCurrentPassword, changePassword } = await import("../../services/authApi");
            const user = (await import("../../utils/authStorage")).getCurrentUser();
            const verifyRes = await verifyCurrentPassword({ userId: user.id, password: currentPassword });
            if (!verifyRes.data.success) {
                setPasswordError("Current password is incorrect.");
                return;
            }
            // Change password
            const changeRes = await changePassword(user.id, newPassword);
            if (changeRes.data.success) {
                setPasswordError("");
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                if (onClose) onClose();
                toast.success("Password changed successfully.");
            } else {
                setPasswordError(changeRes.data.message || "Failed to change password.");
            }
        } catch (err) {
            setPasswordError("An error occurred. Please try again.");
        }
    };
    const handleLogoChange = (e) => {
        setLogoError("");
        const file = e.target.files[0];
        if (file && file.size > 2 * 1024 * 1024) {
            setLogoError("Logo must be less than 2MB.");
            return;
        }
        setBusiness(prev => ({ ...prev, logo: file }));
        setLogoPreview(file ? URL.createObjectURL(file) : null);
    };
    const handleLogoUpload = (e) => {
        e.preventDefault();
        if (!business.logo) {
            setLogoError("Please select a logo file.");
            return;
        }
        setLogoError("Logo updated! (Demo only)");
    };
    const handleLogout = () => {
        setIsLoggingOut(true);
        alert('Logged out! (Demo only)');
        onClose();
    };

    // Apple-style section list
    function renderSectionList() {
        return (
            <div className="flex flex-col divide-y divide-gray-100">
                {SECTION_LIST.map(s => (
                    <button
                        key={s.key}
                        className="flex items-center justify-between w-full px-5 py-3 bg-transparent hover:bg-gray-50 transition text-left text-base font-normal text-gray-800 focus:outline-none"
                        onClick={() => setSection(s.key)}
                    >
                        <span>{s.label}</span>
                        <span
                            className="text-2xl font-extrabold px-1"
                            style={{
                                color: 'var(--color-primary)',
                                background: 'none',
                                border: 'none',
                                lineHeight: 1,
                                padding: 0,
                                transition: 'color 0.2s'
                            }}
                            onMouseOver={e => { e.currentTarget.style.color = 'var(--color-primary-dark)'; }}
                            onMouseOut={e => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                        >
                            ›
                        </span>
                    </button>
                ))}
            </div>
        );
    }

    // Apple-style section header with back
    function SectionHeader({ title }) {
        return (
            <div className="flex items-center gap-2 px-1 pt-1 pb-3">
                <button
                    onClick={() => setSection(null)}
                    className="text-2xl font-extrabold px-1 focus:outline-none transition-colors"
                    style={{ color: 'var(--color-primary)', background: 'none', border: 'none', lineHeight: 1, padding: 0 }}
                    onMouseOver={e => { e.currentTarget.style.color = 'var(--color-primary-dark)'; }}
                    onMouseOut={e => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                >
                    ←
                </button>
                <span className="text-base font-medium text-gray-900 tracking-tight">{title}</span>
            </div>
        );
    }

    // Section content renderers (minimal, demo only)
    function renderSectionContent() {
        switch (section) {
            case 'profile':
                return <><SectionHeader title="Profile" />
                    <ProfileSection
                        profile={profile}
                        profilePreview={profilePreview}
                        onChange={handleProfileChange}
                        onPicChange={handleProfilePicChange}
                        setProfile={setProfile}
                        handleSaveProfile={handleSaveProfile}
                        isSavingProfile={isSavingProfile}
                    />
                </>;
            case 'security':
                return <><SectionHeader title="Account Security" />
                    <SecuritySection
                        currentPassword={currentPassword}
                        newPassword={newPassword}
                        confirmPassword={confirmPassword}
                        passwordError={passwordError}
                        twoFactor={twoFactor}
                        onCurrentPasswordChange={e => setCurrentPassword(e.target.value)}
                        onNewPasswordChange={e => setNewPassword(e.target.value)}
                        onConfirmPasswordChange={e => setConfirmPassword(e.target.value)}
                        onTwoFactorChange={e => setTwoFactor(e.target.checked)}
                        onSubmit={handlePasswordChange}
                    />
                </>;
            case 'brand':
                return <><SectionHeader title="Business/Brand" />
                    <BrandSection
                        business={business}
                        logoPreview={logoPreview}
                        logoError={logoError}
                        onNameChange={e => setBusiness(prev => ({ ...prev, name: e.target.value }))}
                        onLogoChange={handleLogoChange}
                        onLogoUpload={handleLogoUpload}
                    />
                </>;
            case 'notifications':
                return <><SectionHeader title="Notifications" />
                    <NotificationsSection
                        emailNotif={emailNotif}
                        smsNotif={smsNotif}
                        systemAlerts={systemAlerts}
                        onEmailChange={e => setEmailNotif(e.target.checked)}
                        onSmsChange={e => setSmsNotif(e.target.checked)}
                        onSystemAlertsChange={e => setSystemAlerts(e.target.checked)}
                    />
                </>;
            case 'appearance':
                return <><SectionHeader title="Theme/Appearance" />
                    <AppearanceSection />
                </>;
            case 'other':
                return <><SectionHeader title="Other" />
                    <OtherSection
                        language={language}
                        onLanguageChange={e => setLanguage(e.target.value)}
                        onLogout={handleLogout}
                    />
                </>;
            default:
                return null;
        }
    }

    const modalContent = (
        <div className="fixed inset-0 z-[9999] bg-black/30 flex items-center justify-center p-4">
            <div className="bg-[#fafbfc] rounded-2xl shadow border border-gray-100 w-full max-w-md mx-auto relative p-0 overflow-y-auto max-h-[90vh]">
                {/* Close button */}
                <button
                    onClick={() => {
                        if (!isLoggingOut) onClose();
                    }}
                    className="absolute top-3 right-3 text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] text-3xl font-extrabold focus:outline-none transition-colors"
                    aria-label="Close settings"
                    style={{ zIndex: 10, background: 'none', border: 'none', lineHeight: 1, padding: 0 }}
                >
                    ×
                </button>
                {/* Title */}
                <div className="sticky top-0 z-5 bg-[#fafbfc] rounded-t-2xl px-6 pt-6 pb-2 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900 tracking-tight">Settings</h2>
                </div>
                <div className="py-1">
                    {section ? renderSectionContent() : renderSectionList()}
                </div>
            </div>
        </div>
    );

    if (typeof document === "undefined") return modalContent;
    return createPortal(modalContent, document.body);
}
